import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2, AlertCircle, CheckCircle2, TrendingUp, TrendingDown,
  Zap, Star, MessageCircle, CreditCard, Calendar, History, XCircle,
  Receipt, Building2, BanIcon, ShieldCheck, FileText,
} from 'lucide-react';
import {
  planApi, receiptInfoApi, PlanCode, PlanInfo, UsageInfo, PlanChangeResponse, PaymentRecord,
  SubscriptionStatus, ReceiptType, CashReceiptPurpose, ReceiptInfo,
} from '@/api/planApi';

// ── 증빙 설정 헬퍼 ───────────────────────────────────────────

function fmtPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function fmtBizNo(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

interface ReceiptFormState {
  receiptType: ReceiptType;
  cashReceiptPurpose: CashReceiptPurpose;
  phoneNumber: string;
  cashReceiptBizNo: string;
  taxInvoiceBizNo: string;
  companyName: string;
  ceoName: string;
  invoiceEmail: string;
}

const EMPTY_RECEIPT_FORM: ReceiptFormState = {
  receiptType: 'NONE',
  cashReceiptPurpose: 'PERSONAL_DEDUCTION',
  phoneNumber: '',
  cashReceiptBizNo: '',
  taxInvoiceBizNo: '',
  companyName: '',
  ceoName: '',
  invoiceEmail: '',
};

// ── 플랜 메타 정보 ────────────────────────────────────────────

interface PlanMeta {
  code: PlanCode;
  name: string;
  price: number | null;
  monthlyLimit: number | null;
  features: string[];
  badge?: string;
  badgeColor?: string;
  color: string;
  borderColor: string;
  bgColor: string;
}

const PLANS: PlanMeta[] = [
  {
    code: 'FREE',
    name: '무료',
    price: 0,
    monthlyLimit: 60,
    features: ['월 60건 AI 생성', '기본 기능 제공', 'API 접근'],
    color: 'text-gray-700',
    borderColor: 'border-gray-200',
    bgColor: 'bg-gray-50',
  },
  {
    code: 'STARTER',
    name: '스타트',
    price: 15000,
    monthlyLimit: 500,
    features: ['월 500건 AI 생성', '모든 기능 제공', '서비스 지원', 'API 접근'],
    color: 'text-brand-blue',
    borderColor: 'border-brand-blue-light',
    bgColor: 'bg-blue-50',
  },
  {
    code: 'PROFESSIONAL',
    name: '프로',
    price: 29000,
    monthlyLimit: 2000,
    features: ['월 2,000건 AI 생성', '모든 기능 제공', '우선 지원', 'API 접근'],
    badge: '인기',
    badgeColor: 'bg-brand-orange text-white',
    color: 'text-brand-blue-dark',
    borderColor: 'border-brand-blue',
    bgColor: 'bg-blue-50',
  },
  {
    code: 'ENTERPRISE',
    name: '엔터프라이즈',
    price: null,
    monthlyLimit: null,
    features: ['무제한 AI 생성', '맞춤 기능 제공', '전담 매니저', 'SLA 보장'],
    color: 'text-brand-purple',
    borderColor: 'border-brand-purple-light',
    bgColor: 'bg-purple-50',
  },
];

const PLAN_ORDER: PlanCode[] = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

function getPlanMeta(code: PlanCode): PlanMeta {
  return PLANS.find(p => p.code === code) ?? PLANS[0];
}

function getPlanLevel(code: PlanCode): number {
  return PLAN_ORDER.indexOf(code);
}

function getChangeType(from: PlanCode, to: PlanCode): 'UPGRADE' | 'DOWNGRADE' {
  return getPlanLevel(to) > getPlanLevel(from) ? 'UPGRADE' : 'DOWNGRADE';
}

function localizePlanCodeMessage(message: string): string {
  return message
    .replace(/\bPROFESSIONAL\b/g, '프로')
    .replace(/\bSTARTER\b/g, '스타트')
    .replace(/\bENTERPRISE\b/g, '엔터프라이즈')
    .replace(/\bFREE\b/g, '무료');
}

/** 기존 PlanInfo만 있을 때 subscriptionStatus 추론 (B/E 신규 엔드포인트 준비 전 fallback) */
function deriveSubscriptionStatus(plan: PlanInfo, hasPendingPayment: boolean): SubscriptionStatus {
  if (plan.hasPendingUpgrade || hasPendingPayment) return 'PENDING_UPGRADE';
  if (plan.pendingPlan !== null) return 'PENDING_DOWNGRADE';
  if (plan.currentPlan !== 'FREE') return 'ACTIVE_PAID';
  return 'FREE';
}

const SUBSCRIPTION_STATUS_META: Record<SubscriptionStatus, { label: string; className: string }> = {
  FREE:               { label: '무료 이용 중',       className: 'bg-gray-100 text-gray-600 border-gray-300' },
  PENDING_UPGRADE:    { label: '입금 대기',          className: 'bg-blue-100 text-blue-700 border-blue-300' },
  ACTIVE_PAID:        { label: '유료 이용 중',       className: 'bg-green-100 text-green-700 border-green-300' },
  PENDING_DOWNGRADE:  { label: '다운그레이드 예약',   className: 'bg-amber-100 text-amber-700 border-amber-300' },
};

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function PlanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [pendingPayment, setPendingPayment] = useState<PaymentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);

  // Modal 상태
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; targetPlan: PlanCode | null }>({
    open: false,
    targetPlan: null,
  });
  const [blockModal, setBlockModal] = useState<{ open: boolean; message: string; altPlan?: PlanCode }>({
    open: false,
    message: '',
  });
  const [resultModal, setResultModal] = useState<{ open: boolean; result: PlanChangeResponse | null }>({
    open: false,
    result: null,
  });
  const [changing, setChanging] = useState(false);

  // 증빙 선택 Modal 상태
  const [receiptModal, setReceiptModal] = useState<{ open: boolean; targetPlan: PlanCode | null }>({
    open: false, targetPlan: null,
  });
  const [receiptForm, setReceiptForm] = useState<ReceiptFormState>(EMPTY_RECEIPT_FORM);
  const [receiptHasExisting, setReceiptHasExisting] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptValidating, setReceiptValidating] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [receiptLookupError, setReceiptLookupError] = useState<string | null>(null);
  const [receiptBizValidated, setReceiptBizValidated] = useState(false);
  const [receiptBizStatusLoading, setReceiptBizStatusLoading] = useState(false);
  const [receiptBizStatusWarning, setReceiptBizStatusWarning] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planRes, usageRes, billingRes] = await Promise.all([
        planApi.getMyPlan(),
        planApi.getMyUsage(),
        planApi.getBillingHistory({ page: 0, size: 20 }),
      ]);
      setPlanInfo(planRes.data);
      setUsageInfo(usageRes.data);
      const pendingList = (billingRes.data.content ?? [])
        .filter((item) => item.status === 'PENDING')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setPendingPayment(pendingList[0] ?? null);
    } catch {
      setError('요금제 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── 증빙 설정 핸들러 ────────────────────────────────────────

  const openReceiptModal = async (targetPlan: PlanCode) => {
    setReceiptError(null);
    setReceiptLookupError(null);
    setReceiptBizValidated(false);
    setReceiptBizStatusWarning(null);
    setReceiptModal({ open: true, targetPlan });
    setReceiptLoading(true);
    try {
      const res = await receiptInfoApi.get();
      const info = res.data;
      if (info && info.receiptType) {
        setReceiptHasExisting(true);
        const isCashPersonal = info.receiptType === 'CASH_RECEIPT' && info.cashReceiptPurpose === 'PERSONAL_DEDUCTION';
        const isCashBusiness = info.receiptType === 'CASH_RECEIPT' && info.cashReceiptPurpose === 'BUSINESS_EXPENSE';
        setReceiptForm({
          receiptType: info.receiptType ?? 'NONE',
          cashReceiptPurpose: info.cashReceiptPurpose ?? 'PERSONAL_DEDUCTION',
          phoneNumber: isCashPersonal ? (info.cashReceiptNumberMasked ?? '') : '',
          cashReceiptBizNo: isCashBusiness ? (info.cashReceiptNumberMasked ?? '') : '',
          taxInvoiceBizNo: info.bizRegNoMasked ?? '',
          companyName: info.bizName ?? '',
          ceoName: info.bizCeoName ?? '',
          invoiceEmail: info.taxInvoiceEmail ?? '',
        });
        if (info.bizName || info.bizCeoName) setReceiptBizValidated(true);
      }
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        setReceiptError('증빙 설정을 불러오지 못했습니다.');
      }
      setReceiptHasExisting(false);
      setReceiptForm(EMPTY_RECEIPT_FORM);
    } finally {
      setReceiptLoading(false);
    }
  };

  const isMaskedValue = (v: string) => v.includes('*');

  const validateReceiptForm = (): string | null => {
    const { receiptType, cashReceiptPurpose, phoneNumber, cashReceiptBizNo, taxInvoiceBizNo, invoiceEmail } = receiptForm;
    if (receiptType === 'CASH_RECEIPT') {
      if (cashReceiptPurpose === 'PERSONAL_DEDUCTION') {
        if (!isMaskedValue(phoneNumber)) {
          const d = phoneNumber.replace(/\D/g, '');
          if (!d) return '휴대폰번호를 입력하세요.';
          if (d.length < 10 || d.length > 11) return '유효한 휴대폰번호를 입력하세요.';
          if (!d.startsWith('01')) return '휴대폰번호는 01로 시작해야 합니다.';
        }
      } else {
        if (!isMaskedValue(cashReceiptBizNo)) {
          const d = cashReceiptBizNo.replace(/\D/g, '');
          if (!d) return '사업자등록번호를 입력하세요.';
          if (d.length !== 10) return '사업자등록번호 10자리를 입력하세요.';
        }
      }
    } else if (receiptType === 'TAX_INVOICE') {
      if (!isMaskedValue(taxInvoiceBizNo)) {
        const d = taxInvoiceBizNo.replace(/\D/g, '');
        if (!d) return '사업자등록번호를 입력하세요.';
        if (d.length !== 10) return '사업자등록번호 10자리를 입력하세요.';
      }
      if (!receiptForm.companyName.trim()) return '상호명을 입력하세요.';
      if (!receiptForm.ceoName.trim()) return '대표자명을 입력하세요.';
      if (!receiptBizValidated) return '사업자 진위확인을 완료해 주세요.';
      if (!invoiceEmail.trim()) return '세금계산서 수신 이메일을 입력하세요.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceEmail.trim())) return '유효한 이메일 주소를 입력하세요.';
    }
    return null;
  };

  const handleReceiptBizStatusCheck = async (rawBizNo: string) => {
    setReceiptBizStatusLoading(true);
    setReceiptBizStatusWarning(null);
    try {
      const res = await receiptInfoApi.checkBusinessStatus(rawBizNo);
      const data = res.data;
      if (data.found) {
        if (data.bizStatusCode === '03') {
          setReceiptBizStatusWarning('본 사업자는 폐업 상태이므로 세금계산서 발행이 제한됩니다. 관련 문의는 담당자(ibjun77@gmail.com)로 연락 부탁드립니다.');
        } else if (data.bizStatusCode === '02') {
          setReceiptBizStatusWarning(`${data.bizStatus ?? '휴업'} 사업자입니다. 세금계산서 발행이 불가할 수 있습니다.`);
        }
      }
    } catch {
      // 상태조회 실패는 무시 (진위확인에서 최종 검증)
    } finally {
      setReceiptBizStatusLoading(false);
    }
  };

  const handleReceiptBizValidate = async () => {
    const rawBizNo = receiptForm.taxInvoiceBizNo.replace(/\D/g, '');
    if (rawBizNo.length !== 10) {
      setReceiptLookupError('사업자등록번호 10자리를 입력하세요.');
      return;
    }
    if (!receiptForm.companyName.trim()) {
      setReceiptLookupError('상호명을 입력하세요.');
      return;
    }
    if (!receiptForm.ceoName.trim()) {
      setReceiptLookupError('대표자명을 입력하세요.');
      return;
    }
    setReceiptValidating(true);
    setReceiptLookupError(null);
    try {
      const res = await receiptInfoApi.validateBusiness(receiptForm.taxInvoiceBizNo, receiptForm.companyName.trim(), receiptForm.ceoName.trim());
      const data = res.data;
      if (!data.valid) {
        setReceiptLookupError(data.message ?? '사업자 정보가 일치하지 않습니다. 사업자번호·상호명·대표자명을 다시 확인해 주세요.');
        setReceiptBizValidated(false);
        return;
      }
      setReceiptBizValidated(true);
    } catch (err: any) {
      setReceiptLookupError(err?.response?.data?.message ?? '진위확인 중 오류가 발생했습니다.');
      setReceiptBizValidated(false);
    } finally {
      setReceiptValidating(false);
    }
  };

  const handleReceiptProceed = async () => {
    const validationError = validateReceiptForm();
    if (validationError) {
      setReceiptError(validationError);
      return;
    }
    setReceiptSaving(true);
    setReceiptError(null);
    try {
      const isCash = receiptForm.receiptType === 'CASH_RECEIPT';
      const isTax = receiptForm.receiptType === 'TAX_INVOICE';
      // 마스킹된 값(*포함)은 null로 전송 — B/E가 기존값 유지
      const payload: ReceiptInfo = {
        receiptType: receiptForm.receiptType,
        cashReceiptPurpose: isCash ? receiptForm.cashReceiptPurpose : null,
        phoneNumber: isCash && receiptForm.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && !isMaskedValue(receiptForm.phoneNumber)
          ? receiptForm.phoneNumber.replace(/\D/g, '') : null,
        cashReceiptBizNo: isCash && receiptForm.cashReceiptPurpose === 'BUSINESS_EXPENSE' && !isMaskedValue(receiptForm.cashReceiptBizNo)
          ? receiptForm.cashReceiptBizNo.replace(/\D/g, '') : null,
        taxInvoiceBizNo: isTax && !isMaskedValue(receiptForm.taxInvoiceBizNo) ? receiptForm.taxInvoiceBizNo.replace(/\D/g, '') : null,
        companyName: isTax ? receiptForm.companyName : null,
        ceoName: isTax ? receiptForm.ceoName : null,
        invoiceEmail: isTax ? receiptForm.invoiceEmail.trim() : null,
      };
      await receiptInfoApi.save(payload);
      const targetPlan = receiptModal.targetPlan;
      setReceiptModal({ open: false, targetPlan: null });
      if (targetPlan) setConfirmModal({ open: true, targetPlan });
    } catch (err: any) {
      setReceiptError(err?.response?.data?.message ?? '증빙 설정 저장 중 오류가 발생했습니다.');
    } finally {
      setReceiptSaving(false);
    }
  };

  const handleChangePlan = (target: PlanCode) => {
    if (!planInfo || !usageInfo) return;

    const current = planInfo.currentPlan;
    if (current === target) return;
    const changeType = getChangeType(current, target);

    // 엔터프라이즈는 문의로 이동
    if (target === 'ENTERPRISE') {
      window.location.href = 'mailto:ibjun77@gmail.com?subject=엔터프라이즈 플랜 문의';
      return;
    }

    // P0-T01: PENDING_UPGRADE 상태 — 업그레이드/다운그레이드 전부 차단
    if (subscriptionStatus === 'PENDING_UPGRADE') {
      setBlockModal({
        open: true,
        message: pendingPayment
          ? `${getPlanMeta(pendingPayment.plan).name} 플랜 업그레이드 요청이 입금 대기 중입니다. 입금 확인 후 추가 변경을 요청해 주세요.`
          : '입금 대기 중인 업그레이드 요청이 있습니다. 입금 확인 후 추가 변경을 요청해 주세요.',
      });
      return;
    }

    // P0-T01: ACTIVE_PAID + hasPendingUpgrade — 더 높은 플랜 신청 시 업그레이드 버튼 차단
    if (changeType === 'UPGRADE' && hasPendingUpgrade) {
      setBlockModal({
        open: true,
        message: pendingPayment
          ? `${getPlanMeta(pendingPayment.plan).name} 플랜 업그레이드 요청이 입금 대기 중입니다. 입금 확인 후 추가 업그레이드를 요청해 주세요.`
          : '입금 대기 중인 업그레이드 요청이 있습니다. 입금 확인 후 추가 업그레이드를 요청해 주세요.',
      });
      return;
    }

    // 다운그레이드는 월 1회 제한 적용
    if (changeType === 'DOWNGRADE' && !planInfo.canChangePlan) {
      setBlockModal({
        open: true,
        message: '이번 달 다운그레이드 변경 횟수(1회)를 초과했습니다. 다음 달에 다시 시도해 주세요.',
      });
      return;
    }

    // 다운그레이드 시 사용량 체크
    if (changeType === 'DOWNGRADE') {
      const targetMeta = getPlanMeta(target);
      const limit = targetMeta.monthlyLimit ?? 0;
      if (usageInfo.usedCount > limit) {
        const altPlan = PLANS.find(
          p => p.monthlyLimit !== null && p.monthlyLimit >= usageInfo.usedCount &&
               getPlanLevel(p.code) < getPlanLevel(current) &&
               getPlanLevel(p.code) > getPlanLevel(target)
        );
        setBlockModal({
          open: true,
          message: `이번 달 AI 사용량이 ${usageInfo.usedCount.toLocaleString()}건입니다. ${targetMeta.name} 플랜(${limit.toLocaleString()}건)으로 변경하시면 이번 달 서비스 이용이 제한될 수 있어 변경이 불가합니다.`,
          altPlan: altPlan?.code,
        });
        return;
      }
    }

    // 업그레이드: 증빙 선택 먼저, 다운그레이드: 바로 confirm
    if (changeType === 'UPGRADE') {
      openReceiptModal(target);
    } else {
      setConfirmModal({ open: true, targetPlan: target });
    }
  };

  const handleConfirmChange = async () => {
    if (!confirmModal.targetPlan) return;
    setChanging(true);
    try {
      const res = await planApi.changePlan({ targetPlan: confirmModal.targetPlan });
      setConfirmModal({ open: false, targetPlan: null });
      setResultModal({ open: true, result: res.data });
      await load();
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      setConfirmModal({ open: false, targetPlan: null });
      // P0-T01: TRANSITION_LOCKED — 카운트다운 없이 고정 메시지 처리
      if (status === 409 && code === 'TRANSITION_LOCKED') {
        setError('현재 처리 중인 플랜 변경이 있어 추가 변경을 할 수 없습니다. 잠시 후 다시 시도해 주세요.');
      } else if (status === 409) {
        const msg = err?.response?.data?.message ?? '이미 처리 중인 변경이 있습니다.';
        setError(localizePlanCodeMessage(msg));
      } else {
        const msg = err?.response?.data?.message ?? '요금제 변경 요청 중 오류가 발생했습니다.';
        setError(localizePlanCodeMessage(msg));
      }
    } finally {
      setChanging(false);
    }
  };

  /** P0-T01: PENDING_DOWNGRADE 취소 (다운그레이드 예약 철회 → POST /api/my/plan/change with current plan) */
  const handleCancelPendingDowngrade = async () => {
    if (!planInfo) return;
    setCancellingDowngrade(true);
    setError(null);
    try {
      // 현재 플랜으로 다시 신청 → B/E 상태머신이 PENDING_DOWNGRADE → ACTIVE_PAID 전이 처리
      await planApi.changePlan({ targetPlan: planInfo.currentPlan });
      await load();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setError('현재 처리 중인 변경이 있어 취소할 수 없습니다. 잠시 후 다시 시도해 주세요.');
      } else {
        const msg = err?.response?.data?.message ?? '다운그레이드 예약 취소 처리 중 오류가 발생했습니다.';
        setError(localizePlanCodeMessage(msg));
      }
    } finally {
      setCancellingDowngrade(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentPlan = planInfo?.currentPlan ?? user?.subscriptionPlan ?? 'FREE';
  const currentMeta = getPlanMeta(currentPlan as PlanCode);
  const hasPendingUpgrade = (planInfo?.hasPendingUpgrade === true) || !!pendingPayment;
  const usageRate = usageInfo?.usageRate ?? 0;
  const usedCount = usageInfo?.usedCount ?? 0;
  const monthlyLimit = usageInfo?.monthlyLimit ?? currentMeta.monthlyLimit ?? 0;

  // P0-T01: subscriptionStatus 확정
  // /api/my/plan 응답의 subscriptionStatus 필드 우선, 없으면 기존 필드로 추론
  const subscriptionStatus: SubscriptionStatus = planInfo?.subscriptionStatus
    ?? deriveSubscriptionStatus(planInfo ?? { currentPlan: 'FREE', pendingPlan: null, hasPendingUpgrade: false } as PlanInfo, hasPendingUpgrade);
  const statusMeta = SUBSCRIPTION_STATUS_META[subscriptionStatus];
  const pendingDowngradePlan = planInfo?.pendingPlan as PlanCode | null;
  const effectiveDate = planInfo?.planExpiryDate ?? null;

  return (
    <div className="space-y-6 p-4 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">요금제 관리</h1>
          <p className="text-sm text-gray-500 mt-1">현재 이용 중인 요금제를 확인하고 변경할 수 있습니다.</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-1.5"
          onClick={() => navigate('/dashboard/my/subscription-history')}
        >
          <History className="w-4 h-4" />
          상태 변경 이력
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* P0-T01: 구독 상태 배지 */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500">구독 상태:</span>
        <Badge className={`border ${statusMeta.className}`}>{statusMeta.label}</Badge>
        {subscriptionStatus === 'PENDING_UPGRADE' && (
          <span className="text-xs text-blue-600">
            입금 확인이 완료되면 자동으로 플랜이 적용됩니다. 미입금 시 24시간 후 자동 취소됩니다.
          </span>
        )}
        {subscriptionStatus === 'PENDING_DOWNGRADE' && pendingDowngradePlan && effectiveDate && (
          <span className="text-xs text-amber-700">
            {new Date(effectiveDate).toLocaleDateString('ko-KR')} 에 {getPlanMeta(pendingDowngradePlan).name} 플랜으로 전환 예정
          </span>
        )}
      </div>

      {/* P0-T01: PENDING_DOWNGRADE 취소 버튼 */}
      {subscriptionStatus === 'PENDING_DOWNGRADE' && pendingDowngradePlan && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-amber-800 text-sm">
              {effectiveDate
                ? `${new Date(effectiveDate).toLocaleDateString('ko-KR')} 에 ${getPlanMeta(pendingDowngradePlan).name} 플랜으로 전환됩니다.`
                : `${getPlanMeta(pendingDowngradePlan).name} 플랜으로 다운그레이드가 예약되어 있습니다.`}
              {' '}당월 말까지는 현재 플랜 혜택이 유지됩니다.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
              disabled={cancellingDowngrade}
              onClick={handleCancelPendingDowngrade}
            >
              {cancellingDowngrade
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />취소 중...</>
                : <><XCircle className="w-3.5 h-3.5 mr-1" />다운그레이드 예약 취소</>}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* 현재 요금제 요약 카드 */}
      <Card className={`border-2 ${currentMeta.borderColor} ${currentMeta.bgColor}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Zap className={`w-5 h-5 ${currentMeta.color}`} />
              <CardTitle className={`text-lg ${currentMeta.color}`}>현재 플랜: {currentMeta.name}</CardTitle>
            </div>
            {planInfo?.pendingPlan && (
              <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300">
                다음 달 {getPlanMeta(planInfo.pendingPlan).name} 전환 예정
              </Badge>
            )}
            {hasPendingUpgrade && (
              <Badge className="bg-blue-100 text-blue-700 border border-blue-300">
                {pendingPayment
                  ? `업그레이드 입금대기: ${getPlanMeta(pendingPayment.plan).name}`
                  : '업그레이드 입금대기 진행중'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 사용량 프로그레스 바 */}
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">이번 달 AI 사용량</span>
              <span className="font-semibold">
                {usedCount.toLocaleString()} / {monthlyLimit > 0 ? monthlyLimit.toLocaleString() : '무제한'}건
                {monthlyLimit > 0 && <span className="text-gray-400 ml-1">({Math.round(usageRate)}%)</span>}
              </span>
            </div>
            {monthlyLimit > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    usageRate >= 100 ? 'bg-red-500' : usageRate >= 80 ? 'bg-orange-400' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(usageRate, 100)}%` }}
                />
              </div>
            )}
            {usageRate >= 80 && usageRate < 100 && (
              <p className="text-xs text-orange-600 mt-1">사용량이 80%를 초과했습니다. 상위 플랜 업그레이드를 고려해보세요.</p>
            )}
            {usageRate >= 100 && (
              <p className="text-xs text-red-600 mt-1">이번 달 사용량을 모두 소진했습니다. 업그레이드 후 계속 이용 가능합니다.</p>
            )}
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            {planInfo?.planExpiryDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                다음 갱신일: {new Date(planInfo.planExpiryDate).toLocaleDateString('ko-KR')}
              </span>
            )}
            {!planInfo?.canChangePlan && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-3.5 h-3.5" />
                이번 달 다운그레이드 변경 완료 (다음 달부터 가능)
              </span>
            )}
            {hasPendingUpgrade && (
              <span className="flex items-center gap-1 text-blue-600">
                <AlertCircle className="w-3.5 h-3.5" />
                {pendingPayment
                  ? `${getPlanMeta(pendingPayment.plan).name} 플랜 업그레이드 요청이 입금대기 중입니다. (요청일: ${new Date(pendingPayment.createdAt).toLocaleDateString('ko-KR')}, 금액: ₩${pendingPayment.amount.toLocaleString()})`
                  : '입금 대기 중인 업그레이드 요청이 있습니다.'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 요금제 비교 카드 */}
      <div>
        <h2 className="text-base font-semibold text-gray-700 mb-3">요금제 비교</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map(plan => {
            const isCurrent = currentPlan === plan.code;
            const changeType = !isCurrent && plan.code !== 'ENTERPRISE'
              ? getChangeType(currentPlan as PlanCode, plan.code)
              : null;
            // P0-T01: subscriptionStatus 기반 버튼 활성 여부
            const canUpgrade = subscriptionStatus !== 'PENDING_UPGRADE' && !hasPendingUpgrade;
            const canDowngrade = planInfo?.canChangePlan ?? true;

            return (
              <Card
                key={plan.code}
                className={`relative border-2 transition-shadow ${
                  isCurrent ? `${plan.borderColor} shadow-md` : 'border-gray-200 hover:shadow-md'
                }`}
              >
                {/* 배지 */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-1">
                  {isCurrent && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 text-white font-semibold whitespace-nowrap">
                      현재 플랜
                    </span>
                  )}
                  {plan.badge && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${plan.badgeColor}`}>
                      {plan.badge}
                    </span>
                  )}
                </div>

                <CardHeader className="pt-6 pb-3 text-center">
                  <p className={`text-base font-bold ${plan.color}`}>{plan.name}</p>
                  <div className="mt-1">
                    {plan.price === null ? (
                      <p className="text-2xl font-bold text-gray-800">문의</p>
                    ) : plan.price === 0 ? (
                      <p className="text-2xl font-bold text-gray-800">무료</p>
                    ) : (
                      <p className="text-2xl font-bold text-gray-800">
                        ₩{plan.price.toLocaleString()}
                        <span className="text-sm font-normal text-gray-500">/월</span>
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5">
                      {plan.monthlyLimit !== null ? `월 ${plan.monthlyLimit.toLocaleString()}건` : '무제한'}
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="pt-0 space-y-3">
                  <ul className="space-y-1.5">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-gray-600">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* 변경 버튼 */}
                  {isCurrent ? (
                    <Button className="w-full" disabled size="sm">현재 이용 중</Button>
                  ) : plan.code === 'ENTERPRISE' ? (
                    <Button
                      className="w-full bg-brand-purple hover:bg-brand-purple-light text-white"
                      size="sm"
                      onClick={() => handleChangePlan('ENTERPRISE')}
                    >
                      <MessageCircle className="w-3.5 h-3.5 mr-1" />문의하기
                    </Button>
                  ) : changeType === 'UPGRADE' ? (
                    <Button
                      className="w-full"
                      size="sm"
                      disabled={!canUpgrade}
                      title={!canUpgrade
                        ? (pendingPayment
                            ? `${getPlanMeta(pendingPayment.plan).name} 플랜 업그레이드 요청이 입금대기 중입니다.`
                            : '입금 대기 중인 업그레이드 요청이 있습니다.')
                        : undefined}
                      onClick={() => handleChangePlan(plan.code)}
                    >
                      <TrendingUp className="w-3.5 h-3.5 mr-1" />업그레이드
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      variant="outline"
                      size="sm"
                      disabled={!canDowngrade}
                      title={!canDowngrade ? '이번 달 다운그레이드는 1회만 가능합니다.' : undefined}
                      onClick={() => handleChangePlan(plan.code)}
                    >
                      <TrendingDown className="w-3.5 h-3.5 mr-1" />다운그레이드
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* 업크레이드 Upsell CTA (사용량 80% 이상) */}
      {usageRate >= 80 && currentPlan !== 'PROFESSIONAL' && currentPlan !== 'ENTERPRISE' && (
        <Card className="border-brand-orange-light bg-orange-50">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-orange-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-orange-800">더 많은 AI 생성이 필요하신가요?</p>
                <p className="text-xs text-orange-600 mt-0.5">
                  {currentPlan === 'FREE'
                    ? '스타트 플랜으로 업그레이드하면 월 500건까지 사용 가능합니다.'
                    : '프로 플랜으로 업그레이드하면 월 2,000건까지 사용 가능합니다.'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="whitespace-nowrap"
              onClick={() => handleChangePlan(currentPlan === 'FREE' ? 'STARTER' : 'PROFESSIONAL')}
            >
              업그레이드
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 증빙 선택 Modal */}
      <Dialog open={receiptModal.open} onOpenChange={(o) => !receiptSaving && setReceiptModal({ open: o, targetPlan: receiptModal.targetPlan })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              증빙 신청 설정
            </DialogTitle>
            <DialogDescription>
              입금 확인 시 자동 발행할 증빙 유형을 선택하세요.
              {receiptHasExisting && ' 기존 설정을 불러왔습니다.'}
            </DialogDescription>
          </DialogHeader>

          {receiptLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-5">
              {receiptError && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>{receiptError}</AlertDescription>
                </Alert>
              )}

              {/* 유형 선택 */}
              <div className="grid grid-cols-3 gap-3">
                {([
                  { type: 'CASH_RECEIPT' as ReceiptType, icon: Receipt, label: '현금영수증', sub: '개인 · 사업자' },
                  { type: 'TAX_INVOICE' as ReceiptType, icon: Building2, label: '세금계산서', sub: '사업자 전용' },
                  { type: 'NONE' as ReceiptType, icon: BanIcon, label: '발행 안함', sub: undefined },
                ]).map(({ type, icon: Icon, label, sub }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { setReceiptForm(prev => ({ ...prev, receiptType: type })); setReceiptError(null); }}
                    className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 py-4 px-3 text-sm font-medium transition-all ${
                      receiptForm.receiptType === type
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${receiptForm.receiptType === type ? 'text-primary' : 'text-gray-400'}`} />
                    <span>{label}</span>
                    {sub && <span className="text-[11px] text-gray-400 font-normal">{sub}</span>}
                  </button>
                ))}
              </div>

              {/* 현금영수증 */}
              {receiptForm.receiptType === 'CASH_RECEIPT' && (
                <div className="space-y-4 pt-2 border-t">
                  <p className="text-sm font-medium text-gray-700">용도 선택</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {([
                      { purpose: 'PERSONAL_DEDUCTION' as CashReceiptPurpose, label: '개인 소득공제용', sub: '휴대폰번호 입력' },
                      { purpose: 'BUSINESS_EXPENSE' as CashReceiptPurpose, label: '사업자 지출증빙용', sub: '사업자번호 입력' },
                    ]).map(({ purpose, label, sub }) => (
                      <button
                        key={purpose}
                        type="button"
                        onClick={() => setReceiptForm(prev => ({ ...prev, cashReceiptPurpose: purpose }))}
                        className={`flex items-center gap-3 rounded-lg border-2 py-3 px-4 text-left text-sm transition-all ${
                          receiptForm.cashReceiptPurpose === purpose
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${receiptForm.cashReceiptPurpose === purpose ? 'border-primary' : 'border-gray-300'}`}>
                          {receiptForm.cashReceiptPurpose === purpose && <span className="w-2 h-2 rounded-full bg-primary" />}
                        </span>
                        <div>
                          <div className="font-medium">{label}</div>
                          <div className="text-xs text-gray-400 font-normal">{sub}</div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {receiptForm.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && (
                    <div className="space-y-1.5">
                      <Label htmlFor="r-phone">휴대폰번호 <span className="text-red-500">*</span></Label>
                      <Input
                        id="r-phone"
                        type="tel"
                        placeholder="010-0000-0000"
                        value={receiptForm.phoneNumber}
                        onChange={e => setReceiptForm(prev => ({ ...prev, phoneNumber: fmtPhone(e.target.value) }))}
                        maxLength={13}
                      />
                      <p className="text-xs text-gray-400">국세청에 등록된 휴대폰번호로 소득공제가 처리됩니다.</p>
                    </div>
                  )}

                  {receiptForm.cashReceiptPurpose === 'BUSINESS_EXPENSE' && (
                    <div className="space-y-1.5">
                      <Label htmlFor="r-cash-biz">사업자등록번호 <span className="text-red-500">*</span></Label>
                      <Input
                        id="r-cash-biz"
                        placeholder="000-00-00000"
                        value={receiptForm.cashReceiptBizNo}
                        onChange={e => setReceiptForm(prev => ({ ...prev, cashReceiptBizNo: fmtBizNo(e.target.value) }))}
                        maxLength={12}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* 세금계산서 */}
              {receiptForm.receiptType === 'TAX_INVOICE' && (
                <div className="space-y-4 pt-2 border-t">
                  <div className="space-y-1.5">
                    <Label htmlFor="r-tax-biz">사업자등록번호 <span className="text-red-500">*</span></Label>
                    <Input
                      id="r-tax-biz"
                      placeholder="000-00-00000"
                      value={receiptForm.taxInvoiceBizNo}
                      onChange={e => {
                        const formatted = fmtBizNo(e.target.value);
                        setReceiptForm(prev => ({ ...prev, taxInvoiceBizNo: formatted }));
                        setReceiptBizValidated(false);
                        setReceiptBizStatusWarning(null);
                        setReceiptLookupError(null);
                        const raw = formatted.replace(/\D/g, '');
                        if (raw.length === 10) handleReceiptBizStatusCheck(raw);
                      }}
                      maxLength={12}
                    />
                    {receiptBizStatusLoading && (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        사업자 상태 조회 중...
                      </p>
                    )}
                    {receiptBizStatusWarning && (
                      <Alert className="border-orange-300 bg-orange-50 py-2">
                        <AlertCircle className="w-3.5 h-3.5 text-orange-500" />
                        <AlertDescription className="text-orange-700 text-xs">{receiptBizStatusWarning}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="r-company">상호명 <span className="text-red-500">*</span></Label>
                    <Input
                      id="r-company"
                      placeholder="상호명을 입력하세요"
                      value={receiptForm.companyName}
                      onChange={e => {
                        setReceiptForm(prev => ({ ...prev, companyName: e.target.value }));
                        setReceiptBizValidated(false);
                        setReceiptLookupError(null);
                      }}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="r-ceo">대표자명 <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2">
                      <Input
                        id="r-ceo"
                        placeholder="대표자명을 입력하세요"
                        value={receiptForm.ceoName}
                        onChange={e => {
                          setReceiptForm(prev => ({ ...prev, ceoName: e.target.value }));
                          setReceiptBizValidated(false);
                          setReceiptLookupError(null);
                        }}
                      />
                      <Button type="button" variant="outline" className="flex-shrink-0" disabled={receiptValidating} onClick={handleReceiptBizValidate}>
                        {receiptValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        <span className="ml-1.5">진위확인</span>
                      </Button>
                    </div>
                    {receiptLookupError && <p className="text-xs text-red-500">{receiptLookupError}</p>}
                    {receiptBizValidated && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        진위확인이 완료되었습니다.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="r-email">세금계산서 수신 이메일 <span className="text-red-500">*</span></Label>
                    <Input
                      id="r-email"
                      type="email"
                      placeholder="email@example.com"
                      value={receiptForm.invoiceEmail}
                      onChange={e => setReceiptForm(prev => ({ ...prev, invoiceEmail: e.target.value }))}
                    />
                    <p className="text-xs text-gray-400">국세청 전자세금계산서가 이메일로 발송됩니다.</p>
                  </div>

                  <Alert className="border-orange-200 bg-orange-50">
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                    <AlertDescription className="text-orange-700 text-xs">
                      세금계산서는 사업자만 신청 가능합니다. 사업자번호·상호명·대표자명 입력 후 진위확인을 완료해 주세요.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* 발행 안함 */}
              {receiptForm.receiptType === 'NONE' && (
                <div className="pt-2 border-t">
                  <Alert className="border-gray-200 bg-gray-50">
                    <AlertDescription className="text-gray-600 text-sm">
                      증빙이 발행되지 않습니다. 나중에 설정에서 언제든지 변경할 수 있습니다.
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setReceiptModal({ open: false, targetPlan: null })} disabled={receiptSaving}>
                  취소
                </Button>
                <Button onClick={handleReceiptProceed} disabled={receiptSaving}>
                  {receiptSaving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
                  저장 후 계속
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 변경 확인 Modal */}
      <Dialog open={confirmModal.open} onOpenChange={(o) => !changing && setConfirmModal({ open: o, targetPlan: confirmModal.targetPlan })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>요금제 변경 확인</DialogTitle>
            <DialogDescription>변경 전 아래 내용을 확인해 주세요.</DialogDescription>
          </DialogHeader>
          {confirmModal.targetPlan && planInfo && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 전</span>
                  <span className="font-semibold">{getPlanMeta(planInfo.currentPlan).name} 플랜</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 후</span>
                  <span className="font-semibold">{getPlanMeta(confirmModal.targetPlan).name} 플랜</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 유형</span>
                  <Badge className={
                    getChangeType(planInfo.currentPlan, confirmModal.targetPlan) === 'UPGRADE'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }>
                    {getChangeType(planInfo.currentPlan, confirmModal.targetPlan) === 'UPGRADE' ? '업그레이드' : '다운그레이드'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">적용 시점</span>
                  <span className="font-semibold">
                    {getChangeType(planInfo.currentPlan, confirmModal.targetPlan) === 'UPGRADE'
                      ? '입금 확인 후 즉시 적용'
                      : '다음 달 1일 00:00 적용'}
                  </span>
                </div>
              </div>

              {getChangeType(planInfo.currentPlan, confirmModal.targetPlan) === 'UPGRADE' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-xs">
                    업그레이드는 계좌이체 결제 후 관리자 입금 확인 시 즉시 적용됩니다.
                    결제 안내는 <strong>결제 안내</strong> 페이지에서 확인하세요.
                  </AlertDescription>
                </Alert>
              )}

              {getChangeType(planInfo.currentPlan, confirmModal.targetPlan) === 'DOWNGRADE' && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 text-xs">
                    다운그레이드는 다음 달 1일부터 적용됩니다. 당월 말까지는 현재 플랜 혜택이 유지됩니다.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmModal({ open: false, targetPlan: null })} disabled={changing}>
                  취소
                </Button>
                <Button onClick={handleConfirmChange} disabled={changing}>
                  {changing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  변경 요청
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 변경 불가 Modal */}
      <Dialog open={blockModal.open} onOpenChange={(o) => setBlockModal({ ...blockModal, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>요금제 변경 불가</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{blockModal.message}</AlertDescription>
            </Alert>
            {blockModal.altPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-700">
                <p className="font-semibold mb-1">대안 제안</p>
                <p>{getPlanMeta(blockModal.altPlan).name} 플랜({getPlanMeta(blockModal.altPlan).monthlyLimit?.toLocaleString()}건)으로 변경해보세요.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              {blockModal.altPlan && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setBlockModal({ open: false, message: '' });
                    if (blockModal.altPlan) setConfirmModal({ open: true, targetPlan: blockModal.altPlan });
                  }}
                >
                  {getPlanMeta(blockModal.altPlan).name} 플랜으로 변경
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => window.location.href = 'mailto:ibjun77@gmail.com?subject=요금제 변경 문의'}
              >
                관리자 문의
              </Button>
              <Button onClick={() => setBlockModal({ open: false, message: '' })}>확인</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 변경 결과 Modal */}
      <Dialog open={resultModal.open} onOpenChange={(o) => setResultModal({ open: o, result: resultModal.result })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              요금제 변경 요청 완료
            </DialogTitle>
          </DialogHeader>
          {resultModal.result && (
            <div className="space-y-4 text-sm">
              <p className="text-gray-700">{resultModal.result.message}</p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-gray-500">적용 예정일</span>
                  <span className="font-semibold">
                    {new Date(resultModal.result.effectiveDate).toLocaleDateString('ko-KR')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">변경 유형</span>
                  <span className="font-semibold">
                    {resultModal.result.changeType === 'UPGRADE' ? '업그레이드' : '다운그레이드'}
                  </span>
                </div>
              </div>
              {resultModal.result.changeType === 'UPGRADE' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-xs">
                    결제 안내 페이지에서 입금 계좌를 확인하고 입금해 주세요. 입금 확인 후 즉시 플랜이 적용됩니다.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setResultModal({ open: false, result: null })}>확인</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
